import { ICommandBarItemProps, IImageProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCommandBar, IPDefaultButton, IPImage, IPSpinner, IPText } from 'baseComponents';
import { IPImageFit } from 'baseComponents/Image/IPImage';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { getOfficeOnlineViewerUrl, needsOfficeOnlineViewer } from 'components/Links/Helpers/officeOnlineViewer';
import {
  cancelIcon,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalTextStylesBold,
  openExtIcon,
} from 'globalStyles';
import { getListCapabilities, getProviderName } from 'models/documentProvider';
import { PublishingStateSharePoint, ResourceLinkVersion } from 'models/resourceLink';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiGetLinkContentFile,
  apiGetLinkContentPage,
  apiGetLinkContentPreview,
  apiGetLinkContentPreviewUrl,
  apiGetLinkEmbedUrl,
  apiGetLinkProviderMetadata,
  apiGetLinkWebUrl,
} from 'services/Api/linkContentService';
import { apiRequest } from 'services/Auth/authConfig';
import Config from 'services/Config/configService';
import logger from 'services/Logging/logService';
import { hasSessionAtProvider } from 'services/Wrappers/linkContent/brokeredLinkContent';
import AppError from 'utils/appError';
import { ControllerExceptionCodes } from 'utils/controllerException';
import { downloadBlob } from 'utils/download';
import { getHtmlJSX } from 'utils/html';
import { navigateToExternalUrl } from 'utils/url';
import { CopilotMarkdown } from '../../Copilot/Panel/Conversation/CopilotMarkdown';
import { PageHeaderDoc } from '../../SharePoint/PageRenderer/SharePointPageHeaders';
import { BrokeredDirectoryExplorer } from '../Brokered/BrokeredDirectoryExplorer';
import { getBrokeredErrorMessage, notifyBrokeredError } from '../Brokered/brokeredErrors';
import { ConnectProviderAccountCard } from '../Brokered/ConnectProviderAccountCard';
import { ILinkPreviewProps } from './LinkPreview';
import { PreviewIFrame } from './PreviewIFrame';

/** Why the item could not be loaded, in the user's language, with the code when the API gave one. */
interface ILoadError {
  message: string;
  code?: ControllerExceptionCodes;
}

/**
 * Preview for a library item held by a document provider (Google Drive, Nextcloud, Notion,
 * Loop). The bytes come from the API broker, because the credentials live on the server and a
 * customer's Nextcloud or a Notion workspace would not answer a browser call anyway.
 *
 * What is shown follows the provider capabilities, in this order: a wiki page renders its
 * markdown; an Office file goes to the Microsoft Office Online viewer when the stage offers it
 * (officeOnlinePreview: the API parks a copy behind a short-lived url, because Microsoft's
 * servers fetch the file themselves); the provider's own preview page is framed when the stage
 * offers it and the person signed in through that provider (embedPreview, Google Drive); a file
 * the browser can display renders in an iframe from an object url; anything else offers the
 * provider link and a download instead of an embed that would not load.
 *
 * A failure is shown where the preview would be, with its own sentence: a revoked credential,
 * a deleted item, a throttle and a file above the viewer's limit are different problems with
 * different fixes, and one silent "not available" hid all of them behind the same words.
 */
export const LinkPreviewBrokered = (props: ILinkPreviewProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['library', 'translation']);
  //starts true for the same reason as the other previews: getData runs from an effect, so a
  //false start shows "not available" before loading has begun
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [objectUrl, setObjectUrl] = useState<string | undefined>(undefined);
  //a page to frame that lives elsewhere: the Office viewer, or the provider's own preview page
  const [frameUrl, setFrameUrl] = useState<string | undefined>(undefined);
  const [pageHtml, setPageHtml] = useState<string | undefined>(undefined);
  const [pageMarkdown, setPageMarkdown] = useState<string | undefined>(undefined);
  const [webUrl, setWebUrl] = useState<string | undefined>(
    props.link?.isWholeList() ? props.link.list?.webURL : undefined,
  );
  const [loadError, setLoadError] = useState<ILoadError | undefined>(undefined);

  const capabilities = getListCapabilities(props.link?.list, appContext.globalDataCache);
  const providerName = getProviderName(props.link?.list?.providerType ?? 0);

  const imageFileNotAvailable: IImageProps = {
    src: `${Config.getImageURL()}/file-not-available.png`,
    imageFit: IPImageFit.contain,
    width: 96,
    height: 96,
  };

  useEffect(() => {
    //a whole-list link has no item behind it: it renders the category's folder instead
    if (props.link && !props.link.isWholeList()) {
      getData();
    } else {
      setIsLoading(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.link]);

  //an object url holds the downloaded bytes alive until it is revoked
  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  //the provider's page can be framed when the stage offers it and the person has a session there
  const providerEmbedAvailable = (): boolean => {
    return (
      capabilities.embedPreview &&
      props.link?.list !== undefined &&
      hasSessionAtProvider(props.link.list.providerType, appContext)
    );
  };

  const getData = async () => {
    if (!props.link) return;

    try {
      setIsLoading(true);
      setLoadError(undefined);
      setObjectUrl(undefined);
      setFrameUrl(undefined);
      setPageHtml(undefined);
      setPageMarkdown(undefined);

      const accessToken: string = await appContext.getAccessToken(apiRequest.scopes);

      if (capabilities.webLink) {
        try {
          setWebUrl(await apiGetLinkWebUrl(props.link.linkId, accessToken));
        } catch (err) {
          //the stored url is what the item was linked with, so it is the honest fallback
          logger.debug('Error while getting the provider web url', err);
          setWebUrl(props.link.linkURL);
        }
      }

      if (capabilities.pagesKind) {
        const page = await apiGetLinkContentPage(props.link.linkId, accessToken);
        //a provider answers with html or with markdown; Notion only knows markdown
        setPageHtml(page.html);
        setPageMarkdown(page.html ? undefined : page.markdown);
      } else if (capabilities.download) {
        //what the item is decides how it is shown; the name alone cannot tell a Google document,
        //which has no extension, from anything else
        const metadata = await apiGetLinkProviderMetadata(props.link.linkId, accessToken);
        const fileName = metadata.name || props.link.linkName;

        if (capabilities.officeOnlinePreview && needsOfficeOnlineViewer(metadata.mimeType, fileName)) {
          setFrameUrl(await getOfficeViewerUrl(props.link.linkId, accessToken));
        } else if (providerEmbedAvailable()) {
          setFrameUrl(await apiGetLinkEmbedUrl(props.link.linkId, accessToken));
        } else if (isPreviewable(metadata.mimeType, fileName)) {
          const blob = await apiGetLinkContentPreview(props.link.linkId, accessToken);
          setObjectUrl(URL.createObjectURL(blob));
        }
      }

      //the parent expects a version to know the content settled; no provider outside
      //SharePoint has one, so this is the same placeholder a virtual item reports
      const versionInfo = new ResourceLinkVersion();
      versionInfo.version = '1.0';
      versionInfo.date = new Date();
      versionInfo.publishingState = PublishingStateSharePoint.Published;

      props.onVersionLoaded?.(versionInfo);
    } catch (err) {
      logger.debug('Error while loading the provider preview', err);
      const appErr = AppError.fromApiError(err);
      setLoadError({
        message: getBrokeredErrorMessage(appErr, t) ?? t('translation:Errors.ApiError.Message'),
        code: AppError.getControllerExceptionCode(appErr),
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * The Office viewer's url for the parked copy. A file above the viewer's limit (DOCPROV0014)
   * still has the provider's own page when that is on offer; otherwise the refusal is shown as is,
   * with the Open and Download commands beside it.
   */
  const getOfficeViewerUrl = async (linkId: number, accessToken: string): Promise<string> => {
    try {
      return getOfficeOnlineViewerUrl(await apiGetLinkContentPreviewUrl(linkId, accessToken));
    } catch (err) {
      const code = AppError.getControllerExceptionCode(err);

      if (code === ControllerExceptionCodes.Code_DocumentProvider_PreviewTooLarge && providerEmbedAvailable()) {
        logger.debug('The Office viewer refused the file by size; showing the provider page instead');

        return await apiGetLinkEmbedUrl(linkId, accessToken);
      }

      throw err;
    }
  };

  const downloadFile = async () => {
    if (!props.link) return;

    try {
      const accessToken: string = await appContext.getAccessToken(apiRequest.scopes);
      const blob = await apiGetLinkContentFile(props.link.linkId, accessToken);

      downloadBlob(blob, props.link.linkName);
    } catch (err) {
      //the preview may sit in a modal, so a refusal the API explained goes to the bottom toast
      notifyBrokeredError(appContext, err, t);
    }
  };

  //an item the provider no longer has cannot be opened or downloaded either
  const itemIsGone = loadError?.code === ControllerExceptionCodes.Code_DocumentProvider_ItemNotFound;
  //the user's own account is not connected to the category's connection yet: a card with a button, not an error
  const needsGrant =
    loadError?.code === ControllerExceptionCodes.Code_DocumentProvider_UserGrantRequired &&
    props.link?.list?.connectionId !== undefined;

  const getCommandBarItems = (): ICommandBarItemProps[] => {
    const items: ICommandBarItemProps[] = [];

    if (props.onClose) {
      items.push({
        key: 'close',
        disabled: false,
        text: t('library:PinnedLinkTabs.Commands.Close'),
        iconProps: cancelIcon,
        onClick: () => {
          if (props.onClose) props.onClose();
        },
      });
    }

    if (capabilities.webLink && webUrl && !itemIsGone) {
      items.push({
        key: 'openInProvider',
        disabled: false,
        text: t('library:PinnedLinkTabs.Commands.OpenInProvider', { provider: providerName }),
        iconProps: openExtIcon,
        onClick: () => {
          window.open(webUrl, '_blank', 'noopener,noreferrer');
        },
      });
    }

    if (capabilities.download && !props.link?.isWholeList() && !itemIsGone) {
      items.push({
        key: 'download',
        disabled: false,
        text: t('library:Preview.Download'),
        iconProps: { iconName: 'Download' },
        onClick: () => {
          downloadFile();
        },
      });
    }

    if (props.extraCommandBarButtons) {
      items.push(...props.extraCommandBarButtons);
    }

    return items;
  };

  const getCommandBar = () => {
    if (props.hideCommandbar) return null;
    const items = getCommandBarItems();
    if (items.length === 0) return null;

    return (
      <IPStack.Item>
        <IPCommandBar items={items} />
      </IPStack.Item>
    );
  };

  //
  // Main render
  //
  if (!props.link) return null;

  //
  // For links of type 'whole list' render the category's folder in view mode, the way the
  // SharePoint preview renders its file picker
  //
  if (props.link.isWholeList()) {
    const list = props.link.list;

    return (
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        {getCommandBar()}
        <IPStack.Item grow>
          <BrokeredDirectoryExplorer
            connectionId={list.connectionId ?? -1}
            containerId={list.spDriveId}
            rootFolderId={list.spDriveItemId}
            providerName={providerName}
          />
        </IPStack.Item>
      </IPStack>
    );
  }

  //a framed page elsewhere and an object url of the bytes render the same way
  const frameSrc = frameUrl || objectUrl;

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      {getCommandBar()}
      {isLoading && (
        <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
          <IPSpinner size={IPSpinnerSize.large} />
        </IPStack>
      )}
      {!isLoading && loadError && needsGrant && (
        <IPStack.Item grow>
          <ConnectProviderAccountCard
            connectionId={props.link.list?.connectionId ?? -1}
            providerName={providerName}
            message={loadError.message}
            onConnected={getData}
          />
        </IPStack.Item>
      )}
      {!isLoading && loadError && !needsGrant && (
        <IPStack verticalFill horizontalAlign="center" verticalAlign="center" tokens={globalStackTokensGapSmall}>
          <IPStack.Item>
            <IPImage {...imageFileNotAvailable} />
          </IPStack.Item>
          <IPStack.Item styles={{ root: { maxWidth: '80%' } }}>
            <IPText block styles={globalTextStylesBold}>
              {loadError.message}
            </IPText>
          </IPStack.Item>
          {webUrl && !itemIsGone && (
            <IPStack.Item styles={{ root: { maxWidth: '80%' } }}>
              <IPDefaultButton
                iconProps={openExtIcon}
                onClick={() => navigateToExternalUrl(webUrl, undefined, '')}
                text={t('library:PinnedLinkTabs.Commands.OpenInProvider', { provider: providerName })}
              />
            </IPStack.Item>
          )}
        </IPStack>
      )}
      {!isLoading && !loadError && pageHtml && (
        <IPStack.Item grow styles={{ root: { overflowY: 'auto', padding: 20 } }}>
          {getHtmlJSX(pageHtml)}
        </IPStack.Item>
      )}
      {!isLoading && !loadError && !pageHtml && pageMarkdown && (
        <IPStack.Item grow styles={{ root: { overflowY: 'auto', padding: 20 } }}>
          <CopilotMarkdown content={pageMarkdown} withTableStyles={true} />
        </IPStack.Item>
      )}
      {!isLoading && !loadError && !pageHtml && !pageMarkdown && frameSrc && (
        <IPStack verticalFill>
          {!props.hideHeader && <PageHeaderDoc link={props.link} activity={undefined} />}
          <IPStack.Item grow>
            <PreviewIFrame title={props.link.linkName} src={frameSrc} webUrl={webUrl} />
          </IPStack.Item>
        </IPStack>
      )}
      {!isLoading && !loadError && !pageHtml && !pageMarkdown && !frameSrc && (
        <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
          <IPText>{props.unavailableText ?? t('library:Preview.NotAvailableProvider')}</IPText>
        </IPStack>
      )}
    </IPStack>
  );
};

const googleNativeMimeTypes = [
  'application/vnd.google-apps.document',
  'application/vnd.google-apps.spreadsheet',
  'application/vnd.google-apps.presentation',
];

/**
 * Whether the preview endpoint hands back something the browser renders on its own. A
 * Google-native document does: the broker exports it as PDF. Office files are deliberately
 * absent: with the Office viewer on offer they went that way above, and without it a provider
 * file behind our broker has no url Microsoft's viewer could read, so those get the open and
 * download commands instead of a blank frame.
 */
const isPreviewable = (mimeType: string | undefined, fileName: string | undefined): boolean => {
  const mime = mimeType?.toLowerCase() ?? '';

  if (googleNativeMimeTypes.includes(mime)) return true;
  if (mime === 'application/pdf' || mime.startsWith('image/') || mime.startsWith('text/')) return true;

  //a provider that reports no type, or only a generic one, is judged by the name as before
  return canEmbedInBrowser(fileName);
};

/** Which files a browser renders on its own, by name, for a provider that does not say what a file is. */
const canEmbedInBrowser = (fileName: string | undefined): boolean => {
  const name = fileName?.toLowerCase() ?? '';

  return ['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.txt', '.md', '.csv', '.html'].some((extension) =>
    name.endsWith(extension),
  );
};
